'use client';

import { useMemo, useState } from 'react';
import { Bell, X } from 'lucide-react';
import type { Booking, Vehicle } from '@/types';
import { parseBookingPage, parseVehiclePage } from '@/lib/booking/laravel-mirror';
import { daysUntil, complianceState, vidangeState } from '@/lib/morocco';
import { formatMoney } from '@/lib/currency';
import { useTenantQuery } from '@/lib/query/useTenantQuery';

interface NotificationsBellProps {
  vehicles: Vehicle[];
  bookings: Booking[];
  onGoto: (section: string) => void;
  currency?: string | null;
}

type Level = 'urgent' | 'soon' | 'info';

interface Note {
  id: string;
  level: Level;
  text: string;
  section: string;
}

const DOT_CLS: Record<Level, string> = {
  urgent: 'bg-danger',
  soon: 'bg-warning',
  info: 'bg-brand',
};

function localISO(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function NotificationsBell({ vehicles: givenVehicles, bookings: givenBookings, onGoto, currency }: NotificationsBellProps) {
  const cur = currency ?? 'MAD';
  const [open, setOpen] = useState(false);
  const attention = useTenantQuery(['vehicles', 'attention'], '/api/staff/vehicles?attention=1&perPage=50', parseVehiclePage);
  const openBookings = useTenantQuery(['bookings', 'open'], '/api/bookings?open=1&perPage=50', parseBookingPage);
  const vehicles = (attention.data?.data as Vehicle[] | undefined) ?? givenVehicles;
  const bookings = (openBookings.data?.data as Booking[] | undefined) ?? givenBookings;

  const notes = useMemo<Note[]>(() => {
    const list: Note[] = [];
    const today = localISO(0);
    const tomorrow = localISO(1);

    // (a) Retours dus sous 24h
    for (const b of bookings) {
      if (!['active', 'confirmed'].includes(b.status)) continue;
      if (b.dropoffDate !== today && b.dropoffDate !== tomorrow) continue;
      const d = daysUntil(b.dropoffDate);
      list.push({
        id: `ret-${b.id}`,
        level: d !== null && d <= 1 ? 'urgent' : 'soon',
        text: `Retour : ${b.customerName} — ${b.vehicleName} ${b.dropoffDate}`,
        section: 'handover',
      });
    }

    // (b) Documents expirants + vidange
    for (const v of vehicles) {
      const docs: { label: string; date?: string }[] = [
        { label: 'Visite technique', date: v.visiteTechniqueExpiry },
        { label: 'Assurance', date: v.assuranceExpiry },
        { label: 'Vignette', date: v.vignetteExpiry },
      ];
      for (const doc of docs) {
        const st = complianceState(doc.date, 30);
        if (st === 'expired') {
          list.push({
            id: `doc-exp-${v.id}-${doc.label}`,
            level: 'urgent',
            text: `${doc.label} expirée — ${v.name} (${v.licensePlate})`,
            section: 'maintenance',
          });
        } else if (st === 'soon') {
          list.push({
            id: `doc-soon-${v.id}-${doc.label}`,
            level: 'soon',
            text: `${doc.label} expire le ${doc.date} — ${v.name}`,
            section: 'maintenance',
          });
        }
      }
      const vid = vidangeState(v);
      if (vid === 'expired') {
        const over = v.nextVidangeKm != null ? v.mileage - v.nextVidangeKm : 0;
        list.push({
          id: `vid-exp-${v.id}`,
          level: 'urgent',
          text: `Vidange en retard — ${v.name} (+${over.toLocaleString('fr-MA')} km)`,
          section: 'maintenance',
        });
      } else if (vid === 'soon') {
        const left = v.nextVidangeKm != null ? v.nextVidangeKm - v.mileage : 0;
        list.push({
          id: `vid-soon-${v.id}`,
          level: 'soon',
          text: `Vidange proche — ${v.name} (${left.toLocaleString('fr-MA')} km restants)`,
          section: 'maintenance',
        });
      }
    }

    // (c) Réservations en attente
    const pending = bookings.filter((b) => b.status === 'pending').length;
    if (pending > 0) {
      list.push({
        id: 'pending-count',
        level: 'info',
        text: `${pending} réservation${pending > 1 ? 's' : ''} en attente de confirmation`,
        section: 'bookings',
      });
    }

    // (d) Soldes impayés
    for (const b of bookings) {
      if (!['active', 'returned'].includes(b.status)) continue;
      const rest = b.totalPrice - (b.avanceAmount ?? 0);
      if (rest > 0) {
        list.push({
          id: `unpaid-${b.id}`,
          level: 'soon',
          text: `Solde impayé : ${b.customerName} — ${formatMoney(rest, cur)}`,
          section: 'analytics',
        });
      }
    }

    const rank: Record<Level, number> = { urgent: 0, soon: 1, info: 2 };
    return list.sort((a, b) => rank[a.level] - rank[b.level]);
  }, [vehicles, bookings, cur]);

  function goto(section: string) {
    setOpen(false);
    onGoto(section);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
        className="relative rounded-full border border-line bg-card p-2 text-ink hover:bg-shell"
      >
        <Bell className="h-4 w-4" />
        {notes.length > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF8A00] px-1 text-[10px] font-bold text-slate-950">
            {notes.length > 99 ? '99+' : notes.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 overflow-hidden rounded-2xl border border-line bg-card shadow-xl">
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <p className="text-xs font-bold text-ink">
              Notifications{notes.length > 0 ? ` (${notes.length})` : ''}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1 text-muted hover:bg-shell"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto p-2">
            {notes.length === 0 && (
              <div className="flex flex-col items-center gap-1 px-3 py-8 text-center">
                <Bell className="h-5 w-5 text-muted" />
                <p className="text-xs text-muted">Rien à signaler</p>
              </div>
            )}
            {notes.map((n) => (
              <div
                key={n.id}
                className="flex items-start gap-2 rounded-xl px-2 py-2 hover:bg-shell"
              >
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT_CLS[n.level]}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-ink">{n.text}</p>
                  <button
                    type="button"
                    onClick={() => goto(n.section)}
                    className="mt-0.5 text-xs font-semibold text-[#FF8A00] hover:underline"
                  >
                    Voir →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
